/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import isNil from 'lodash/isNil';

import config from '../../config';
import ControlButton from '../control/control-button';
import ErrorName from '../error/error-name';
import LoaderContainer from '../loader/loader-container';
import SetupFailure from './setup-failure';
import SetupManual from './setup-manual';
import SetupOrganization from './setup-organization';
import SetupPanelLeft from './setup-panel-left';
import SetupPanelRight from './setup-panel-right';
import SetupQr from './setup-qr';
import SetupSettings from './setup-settings';
import SetupSuccess from './setup-success';
import template from './setup-root.html';

// TODO Univerzální komponenta `FormField`. Nyní se kód opakuje.
// TODO Automatická synchronizace v GUI.
// - Mělo by provádění sync být opravdu tak agresivní? Zařízení již pak nelze jednoduše odinstalovat.
// - Co když bude uživatel chtít zařízení zaregistrovat na jiném serveru? Sync ihned spáruje výchozí server.
// - Detekci vždy nechat potvrdit v GUI.
// - Co třeba tlačítko "Automatic device setup", které by signalizovalo načítání / úspěch / neúspěch.
// TODO Panely na menších rozlišeních překrývají obsah.
// - Zatím se neřešilo, protože není poptávka po takto malých zařízeních.
// - V ideálním případě by se panely měli poskládat pod sebe a za nimi obsah.
export default {
  name: 'SetupRoot',
  template,
  components: {
    LoaderContainer,
    SetupFailure,
    SetupManual,
    SetupOrganization,
    SetupPanelLeft,
    SetupPanelRight,
    SetupQr,
    SetupSettings,
    SetupSuccess
  },
  created() {
    const { $root } = this;
    const { virtualKeyboardManager } = $root;
    this.automaticEnabled = false;
    this.automaticTimeout = null;
    this.deviceId = null;
    this.organization = null;
    // Pracuje se vždy s otiskem, který byl nastavený v okamžiku vytvoření komponenty.
    this.fingerprint = $root.session.fingerprint;
    // Sledování ovládacích tlačítek.
    $root.controlManager.on('buttonDown', this.onButtonDown);
    // Spuštění podpory pro focus.
    $root.focusManager.start();
    // Sledování stavu připojení k síti.
    $root.networkListener.on('onlineChanged', this.onOnlineChanged);
    // Sledování virtuální klávesnice.
    virtualKeyboardManager.on('infoChanged', this.onVirtualKeyboardChanged);
    // Inicializace stavu virtuální klávesnice.
    virtualKeyboardManager
      .getInfo()
      .then((info) => {
        this.rootStyle = this.getRootStyle(info);
      })
      // Spustí automatickou registraci se serverem na základě otisku.
      .then(() => this.startAutomatic())
      .catch((error) => this.handleError(error));
  },
  data() {
    const { serverUrl } = config.setup;
    return {
      defaultServerUrl: serverUrl,
      deviceName: config.setup.deviceName,
      failureMessage: null,
      failureRetryDelay: null,
      failureRetryTime: null,
      isOnline: this.$root.networkListener.isOnline(),
      organizationArray: [],
      page: 'qr',
      password: config.setup.password,
      rootStyle: null,
      serverUrl,
      successTime: null,
      username: config.setup.username
    };
  },
  destroyed() {
    const { $root } = this;
    this.stopAutomatic();
    $root.controlManager.off('buttonDown', this.onButtonDown);
    $root.focusManager.start();
    $root.networkListener.off('onlineChanged', this.onOnlineChanged);
    $root.virtualKeyboardManager.off(
      'infoChanged',
      this.onVirtualKeyboardChanged
    );
  },
  methods: {
    back() {
      // TODO Šlo by zpracovat lépe.
      // - Mohli by sledovat jednotlivé stránky a vyvolávat "back".
      // - Obecně by pomohlo nasazení routeru.
      // - Také by se neměly vyvolávat on* metody.
      switch (this.page) {
        case 'failure':
          return this.onFailureBack();
        case 'manual':
          return this.onManualBack();
        case 'organization':
          return this.onOrganizationBack();
        case 'settings':
          return this.onSettingsBack();
        default:
          // Výchozí operací je vypnutí aplikace.
          return this.$root.appManager.exit();
      }
    },
    getRootStyle(info) {
      if (info.isOverlapping()) {
        // Pokud klávesnice překrývá obsah, vytvoří se místo pro její zobrazení.
        return { paddingBottom: info.getHeight() };
      }
      // Bez virtuální klávesnice se zarovnání na výšku neupravuje.
      return null;
    },
    handleError(error) {
      this.$root.logger.error(error);
    },
    install(deviceId, serverUrl) {
      return new Promise((resolve) =>
        resolve(
          this.$root.device
            .install(deviceId, serverUrl)
            .then(() => this.showSuccess())
        )
      );
    },
    onAutomatic() {
      // Provede se pokus o registraci.
      new Promise((resolve) => resolve(this.registerAutomatic()))
        .catch((error) => this.handleError(error))
        // Pokud nebyla registrace provedena, tak se po zpoždění provede další pokus.
        .finally(() => {
          if (this.automaticEnabled) {
            this.automaticTimeout = setTimeout(
              this.onAutomatic,
              config.setup.automaticDelay
            );
          }
        });
    },
    onButtonDown(controlButton) {
      if (controlButton === ControlButton.Back) {
        this.back();
      }
    },
    onFailureBack() {
      this.showManual();
    },
    onFailureRetry() {
      new Promise((resolve) => {
        const { failureDelayMultiplier, failureDelayMax } = config.setup;
        this.failureRetryDelay = Math.min(
          this.failureRetryDelay * failureDelayMultiplier,
          failureDelayMax
        );
        this.showLoader();
        const { organization } = this;
        if (!isNil(organization)) {
          return resolve(
            this.registerManualComplete(
              this.serverUrl,
              this.username,
              this.password,
              organization.id,
              this.deviceName
            )
          );
        }
        return resolve(
          this.registerManualSimple(
            this.serverUrl,
            this.username,
            this.password,
            this.deviceName
          )
        );
      }).catch((error) => this.showFailure(error));
    },
    onFinish() {
      this.$emit('finish');
    },
    onManual() {
      this.showManual();
    },
    onManualBack() {
      this.showQr();
    },
    onManualSubmit(deviceName, username, password) {
      new Promise((resolve) => {
        // Nový pokus, resetuje se zpoždění.
        this.failureRetryDelay = config.setup.failureDelayBase;
        // Zadané údaje se uloží pro případné opakované pokusy.
        this.deviceName = deviceName;
        this.password = password;
        this.username = username;
        this.showLoader();
        return resolve(
          this.registerManualSimple(
            this.serverUrl,
            username,
            password,
            deviceName
          )
        );
      }).catch((error) => this.showFailure(error));
    },
    onOnlineChanged(online) {
      this.isOnline = online;
    },
    onOrganizationBack() {
      this.showManual();
    },
    onOrganizationSubmit(organization) {
      new Promise((resolve) => {
        // Nový pokus, resetuje se zpoždění.
        this.failureRetryDelay = config.setup.failureDelayBase;
        // Vybraná organizace se uloží pro případné opakované pokusy.
        this.organization = organization;
        this.showLoader();
        return resolve(
          this.registerManualComplete(
            this.serverUrl,
            this.username,
            this.password,
            organization.id,
            this.deviceName
          )
        );
      }).catch((error) => this.showFailure(error));
    },
    onSettings() {
      this.showSettings();
    },
    onSettingsBack() {
      this.showQr();
    },
    onSettingsSubmit(serverUrl) {
      this.serverUrl = serverUrl;
      this.showQr();
    },
    onVirtualKeyboardChanged(info) {
      this.rootStyle = this.getRootStyle(info);
    },
    registerAutomatic() {
      return new Promise((resolve) => {
        // Při synchronizaci se použije adresa serveru přítomná v okamžiku spuštění.
        // Pokud se adresa serveru změní, použije se automaticky při dalším pokusu.
        const { serverUrl } = this;
        return resolve(
          this.$root.setupManager
            .registerAutomatic(serverUrl, this.fingerprint)
            .then((deviceId) => {
              if (isNil(deviceId)) {
                // Pokud zařízení nebylo nalezeno, nic neděláme.
                return;
              }
              return this.install(deviceId, serverUrl);
            })
        );
      });
    },
    registerManualSimple(serverUrl, username, password, deviceName) {
      return new Promise((resolve) =>
        resolve(
          // Odešleme na server přihlašovací údaje a zjistíme seznam organizací.
          this.$root.setupManager
            .fetchOrganizations(serverUrl, username, password)
            .then((organizationArray) => {
              // Zjištěný seznam se uloží pro případné opakované pokusy.
              this.organizationArray = organizationArray;
              // Bez organizací se dále nedá nic dělat.
              if (!organizationArray.length) {
                throw new Error('No organizations available.');
              }
              // Pokud máme na výběr pouze z jedné organizace, vyberem ji bez ptaní.
              if (organizationArray.length === 1) {
                const organization = organizationArray[0];
                // Zjištěná organizace se uloží pro případné opakované pokusy.
                this.organization = organization;
                return this.registerManualComplete(
                  serverUrl,
                  username,
                  password,
                  organization.id,
                  deviceName
                );
              }
              // Pokud je organizací více, zobrazíme jejich seznam.
              this.showOrganization();
            })
        )
      );
    },
    registerManualComplete(
      serverUrl,
      username,
      password,
      organizationId,
      deviceName
    ) {
      return new Promise((resolve) =>
        resolve(
          // Odešleme na server přihlašovací údaje a vybranou organizaci a získáme
          // ID zařízení.
          this.$root.setupManager
            .registerManual(
              serverUrl,
              this.fingerprint,
              username,
              password,
              deviceName,
              organizationId
            )
            .then((deviceId) => {
              // Zjištěná hodnota deviceId se uloží pro případné opakované pokusy.
              this.deviceId = deviceId;
              return this.install(deviceId, serverUrl);
            })
        )
      );
    },
    showFailure(error) {
      this.failureMessage = error.message;
      this.failureRetryTime = Date.now() + this.failureRetryDelay;
      this.page = 'failure';
      if (error.name === ErrorName.SetupError) {
        // Známá registrační chyba se považuje za ošetřenou jejím zobrazením v GUI.
        return;
      }
      // Neznámé chyby se logují na Sentry.
      this.handleError(error);
    },
    showLoader() {
      this.page = 'loader';
    },
    showManual() {
      this.stopAutomatic();
      this.page = 'manual';
    },
    showOrganization() {
      this.page = 'organization';
    },
    showQr() {
      this.startAutomatic();
      this.page = 'qr';
    },
    showSettings() {
      this.page = 'settings';
    },
    showSuccess() {
      this.stopAutomatic();
      this.successTime = Date.now() + config.setup.successDelay;
      this.page = 'success';
    },
    startAutomatic() {
      // Jestli již synchronizace probíhá, tak se nic nedělá.
      if (this.automaticEnabled) {
        return;
      }
      // Jinak se ihned provede první synchronizace.
      this.automaticEnabled = true;
      this.onAutomatic();
    },
    stopAutomatic() {
      clearInterval(this.automaticTimeout);
      this.automaticTimeout = null;
      this.automaticEnabled = false;
    }
  }
};
